/**
 * should handle ALL WhatsOnChain calls, to apply correct adaptive rate limiting
 *
 * IMPORTANT: WoC limits by RATE (requests per second), NOT by concurrency.
 * We use a time-based rate limiter (spacing request *starts* by minIntervalMs),
 * not a concurrency limiter like p-limit.
 */

const Run = require("runlib")
const AsyncRetry = require("async-retry")

import { getLog } from "../helpers/helpers";

// ------------------------------------------------------------------------------------------------
// WhatsonchainConnector – handles all HTTP requests and adaptive rate limiting
// ------------------------------------------------------------------------------------------------

class WhatsonchainConnector {
    /**
     * @param {string} network 'main' | 'test' | 'stn'
     * @param {?string} apiKey
     * @param {?object} logger Optional logger (defaults to getLog)
     * @param {number} ratePerSecond Desired rate limit (requests per second). WoC free tier = 3.
     */
    constructor(network, apiKey, logger = null, ratePerSecond = 3) {
        this.TAG = 'WhatsonchainConnector';
        this.classname = this.TAG + ": "
        const log = getLog(this.classname + "constructor ")
        this.network = network;
        this.apiKey = apiKey ? apiKey.trim() : null;
        this.isApiKey = !!this.apiKey;

        this.logger = logger || getLog(this.classname);

        // time-based rate limiting: space request STARTS by minIntervalMs.
        // this respects WoC's actual rate limit (N requests/sec) regardless of
        // how fast each request completes. requests that take longer than the
        // interval naturally overlap (concurrency), but the rate is always respected.
        this.initialMinIntervalMs = 1000 / ratePerSecond;
        this.minIntervalMs = this.initialMinIntervalMs;
        this._lastStart = 0;
        this._queue = [];
        this._processing = false;
        this._consecutiveSuccesses = 0;

        if (this.isApiKey) {
            this.logger.info(this.TAG, 'Rate limiting disabled (API key present)');
        } else {
            this.logger.info(this.TAG, `Rate limit enabled: ${ratePerSecond} req/sec (min interval ${this.minIntervalMs}ms)`);
        }
    }

    /**
     * @param {string} url
     * @param {object} options fetch options
     * @param {'json'|'text'} responseType how to read the response body.
     *  'json' (default) for JSON endpoints; 'text' for plain-text endpoints like
     *  /tx/{txid}/hex which return raw transaction hex (NOT valid JSON).
     */
    async request(url, options, responseType = 'json') {
        if (!options.cache) options.cache = 'no-cache'
        // properly serialize object bodies for JSON requests — fetch() does NOT
        // auto-stringify plain objects, so without this the body would be sent as
        // the literal string "[object Object]" instead of {"txhex":"..."}
        if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
            options.body = JSON.stringify(options.body)
        }
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                // capture the response body — WhatsOnChain wraps the BSV node's actual
                // rejection reason (e.g. "unexpected response code 500: 258: txn-mempool-conflict")
                // inside the body of a 400/500 response. Without reading it we'd only ever
                // surface "HTTP error! status: 400" and lose the reason WHY it was rejected.
                let bodyText = ''
                try { bodyText = await response.text() } catch (e) { /* body unreadable, ignore */ }
                bodyText = (bodyText || '').trim()
                const err = new Error(`HTTP error! status: ${response.status}${bodyText ? ': ' + bodyText : ''}`)
                err.status = response.status // needed by _rateLimitedRequest's retry logic
                err.body = bodyText          // needed by broadcast() to detect mempool states
                throw err;
            }
            // /tx/{txid}/hex returns raw hex text, not JSON — parsing it as JSON would
            // throw "SyntaxError: Unexpected number in JSON at position 1" because hex
            // starts with digits (e.g. "010000..."), which is not valid JSON.
            if (responseType === 'text') return await response.text();
            return await response.json();
        } catch (error) {
            console.error('Request failed:', error);
            throw error; // Rethrow or handle the error as needed
        }
    }

    // --------------------------------------------------------------------------
    // Time-based rate limiter
    // --------------------------------------------------------------------------

    /**
     * Schedule a function through the rate limiter. Ensures no more than
     * `1/minIntervalMs` requests are *started* per second, regardless of how
     * fast each completes. Multiple requests can be in-flight simultaneously
     * if they take longer than minIntervalMs, but the start rate is always respected.
     */
    async _run(fn) {
        if (this.isApiKey) return fn()  // API key → no rate limiting
        return new Promise((resolve, reject) => {
            this._queue.push({ fn, resolve, reject })
            this._drain()
        })
    }

    async _drain() {
        if (this._processing) return
        this._processing = true
        while (this._queue.length > 0) {
            const now = Date.now()
            const elapsed = now - this._lastStart
            if (elapsed < this.minIntervalMs) {
                await this._sleep(this.minIntervalMs - elapsed)
            }
            this._lastStart = Date.now()
            const { fn, resolve, reject } = this._queue.shift()
            fn().then(resolve).catch(reject)  // fire & forget — allows natural concurrency
        }
        this._processing = false
    }

    /**
     * Called when a request gets a 429 (rate limited by WoC).
     * Doubles the interval (fast penalty) and resets the success counter.
     */
    _on429() {
        if (this.isApiKey) return
        this._consecutiveSuccesses = 0
        const old = this.minIntervalMs
        this.minIntervalMs *= 2
        this.logger.warn(this.TAG, `429 rate limit: backing off ${old}ms → ${this.minIntervalMs}ms between requests`)
    }

    /**
     * Called on each successful (non-429) request.
     * After 10 consecutive successes, reduces the interval by 10% (slow recovery),
     * floored at the initial interval.
     */
    _onSuccess() {
        if (this.isApiKey) return
        this._consecutiveSuccesses++
        if (this._consecutiveSuccesses >= 10) {
            this._consecutiveSuccesses = 0
            const old = this.minIntervalMs
            this.minIntervalMs = Math.max(this.initialMinIntervalMs, this.minIntervalMs * 0.9)
            if (this.minIntervalMs !== old) {
                this.logger.info(this.TAG, `recovery: ${this._consecutiveSuccesses} successes, easing ${old}ms → ${this.minIntervalMs}ms`)
            }
        }
    }

    // --------------------------------------------------------------------------
    // Utility
    // --------------------------------------------------------------------------

    _backoffDelay(attempt) {
        const base = 1000;
        const max = 60000;
        let delay = Math.min(base * (2 ** (attempt - 1)), max);
        delay += Math.random() * 1000; // jitter
        return delay;
    }

    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    _getHeaders() {
        return this.apiKey ? { 'woc-api-key': this.apiKey } : {};
    }

    _buildUrl(endpoint) {
        return `https://api.whatsonchain.com/v1/bsv/${this.network}${endpoint}`;
    }

    /**
     * Generic rate-limited request executor with retry on 429/5xx.
     * Rate limiting (spacing of request starts) is handled by _run().
     * Retry (re-sending the same request after a failure) is handled here.
     * The two mechanisms are complementary: _run() controls WHEN requests start,
     * retry controls WHAT happens when a request fails.
     */
    async _rateLimitedRequest(requestFn) {
        // Lowered from 15 to 5: 15 retries with exponential backoff up to 60s
        // could stall the initial wallet load for tens of seconds on a single
        // transient 429/5xx. 5 retries (6 attempts total) is enough to ride out
        // brief rate-limit blips without freezing startup for a minute.
        const maxRetries = 5;
        let attempt = 0;

        return this._run(async () => {
            const execute = async () => {
                attempt++;

                try {
                    const result = await requestFn();
                    this._onSuccess();
                    return result;
                } catch (error) {
                    // Robust status extraction
                    let status = null;
                    if (error && typeof error.status === 'number') status = error.status;
                    else if (error && error.response && typeof error.response.status === 'number') status = error.response.status;
                    else if (error && typeof error.statusCode === 'number') status = error.statusCode;

                    if (status === 429) {
                        this._on429();
                    }

                    const isRetryable = status === 429 ||
                        status >= 500 ||
                        ['ETIMEDOUT', 'ECONNABORTED', 'ECONNRESET'].includes(error.code);

                    if (isRetryable && attempt <= maxRetries) {
                        const delay = this._backoffDelay(attempt);
                        this.logger.warn(this.TAG, `Request failed (attempt ${attempt}): ${status || error.code || error.message} | retry in ${delay}ms`);
                        await this._sleep(delay);
                        return execute();
                    }

                    throw error;
                }
            };
            return execute();
        });
    }

    // --------------------------------------------------------------------------
    // Public API methods
    // --------------------------------------------------------------------------

    async broadcast(rawtx) {
        const function_id = this.classname+": broadcast: "
        const log = getLog(function_id)

        const url = this._buildUrl('/tx/raw');
        const headers = { ...this._getHeaders(), 'Content-Type': 'application/json' };

        // use responseType:'text' so we get the RAW body, then parse it ourselves —
        // WoC's /tx/raw response format is inconsistent: it may return the txid as a
        // bare hex string, a JSON-quoted string, or a JSON object {"txid":"..."}.
        const text = await this._rateLimitedRequest(async () => {
            return await this.request(url, {
                method: 'POST',
                body: { txhex: rawtx },
                headers
            }, 'text');
        });
        log("res:", text)

        // callers (CustomBlockchain._broadcast / Run SDK's BlockchainWrapper) expect a
        // bare txid STRING. handle all possible WoC response formats:
        if (text) {
            const trimmed = text.trim();
            // try JSON first (handles {"txid":"..."} and "..." quoted-string forms)
            try {
                const parsed = JSON.parse(trimmed);
                if (typeof parsed === 'string' && parsed.length > 0) return parsed;
                if (parsed && typeof parsed === 'object' && parsed.txid) return parsed.txid;
            } catch (e) {
                // not valid JSON → treat as a bare text txid (hex string)
                if (trimmed.length > 0) return trimmed;
            }
        }
        return "";
    }

    async fetchTxHex(txid) {
        const url = this._buildUrl(`/tx/${txid}/hex`);
        const headers = this._getHeaders();

        return this._rateLimitedRequest(async () => {
            // /tx/{txid}/hex returns raw hex text, NOT JSON — must use responseType:'text'
            return await this.request(url, { headers }, 'text');
        });
    }

    async fetchTxInfo(txid) {
        const url = this._buildUrl(`/tx/hash/${txid}`);
        const headers = this._getHeaders();

        return this._rateLimitedRequest(async () => {
            return await this.request(url, { headers });
        });
    }

    async fetchUtxosByScripthash(scripthash) {
        const url = this._buildUrl(`/script/${scripthash}/unspent`);
        const headers = this._getHeaders();

        return this._rateLimitedRequest(async () => {
            return await this.request(url, { headers });
        });
    }

    /**
     * Fetch the full tx-hash history for an address (confirmed + unconfirmed).
     * GET /address/{address}/history
     * Returns an array of { tx_hash, height } (latest first after caller reverses).
     * A 404 (address not found on chain) returns an empty array instead of throwing.
     */
    async fetchAddressHistory(address) {
        const url = this._buildUrl(`/address/${address}/history`);
        const headers = this._getHeaders();

        return this._rateLimitedRequest(async () => {
            try {
                return await this.request(url, { headers });
            } catch (error) {
                if (error && error.status === 404) {
                    this.logger.info(this.TAG, `fetchAddressHistory: 404 for ${address}, returning []`);
                    return [];
                }
                throw error;
            }
        });
    }

    /**
     * Fetch block data by height.
     * GET /block/height/{height}
     * Returns { time, ... } among other fields.
     */
    async fetchBlockByHeight(height) {
        const url = this._buildUrl(`/block/height/${height}`);
        const headers = this._getHeaders();

        return this._rateLimitedRequest(async () => {
            return await this.request(url, { headers });
        });
    }

    /**
     * Fetch current chain info (latest block height, etc.).
     * GET /chain/info
     * Returns { blocks, ... }.
     */
    async fetchChainInfo() {
        const url = this._buildUrl('/chain/info');
        const headers = this._getHeaders();

        return this._rateLimitedRequest(async () => {
            return await this.request(url, { headers });
        });
    }
}


// ------------------------------------------------------------------------------------------------
// Singleton — shared across the entire app (blockchain + state client)
// ------------------------------------------------------------------------------------------------

let _sharedWhatsOnChainConnector = null

/**
 * Create the singleton with explicit config. Called once per run/blockchain lifecycle
 * (by WhatsOnChainCustom constructor). Throws if already created — call
 * resetSharedWhatsOnChainConnector() first when recreating the entire run stack
 * (e.g. on wallet reload via init_run_and_caches_from_privkeys).
 */
export function createSharedWhatsOnChainConnector(network, apiKey, logger, ratePerSecond) {
    if (_sharedWhatsOnChainConnector) {
        throw new Error('createSharedWhatsOnChainConnector: already created')
    }
    _sharedWhatsOnChainConnector = new WhatsonchainConnector(network, apiKey, logger, ratePerSecond)
    return _sharedWhatsOnChainConnector
}

/**
 * Get the singleton. Must have been created first via createSharedWhatsOnChainConnector(),
 * otherwise throws — prevents silent fallback to default config.
 */
export function getSharedWhatsOnChainConnector() {
    if (!_sharedWhatsOnChainConnector) {
        throw new Error('getSharedWhatsOnChainConnector: not yet created — call createSharedWhatsOnChainConnector() first')
    }
    return _sharedWhatsOnChainConnector
}

/**
 * Clear the singleton so it can be recreated with new config.
 * Call this ONLY when the entire run/blockchain stack is being rebuilt
 * (e.g. init_run_and_caches_from_privkeys creates a new CustomBlockchain →
 * new WhatsOnChainCustom → createSharedWhatsOnChainConnector).
 * Any in-flight requests on the old instance just finish on their own.
 */
export function resetSharedWhatsOnChainConnector() {
    _sharedWhatsOnChainConnector = null
}

/**
 * Async getter that waits (with exponential backoff) for the singleton to be
 * created by init_run_and_caches_from_privkeys() during the wallet's first
 * reload_wallet(). Use this from page-level / UserContext callers that can
 * fire BEFORE that first reload has finished creating the connector — e.g. a
 * user navigating directly to the BSV history page on app startup, which
 * mounts the page and triggers fetch_history() while reload_wallet() is still
 * racing to set up the run/blockchain stack (Display_Wallet's first-load
 * effect defers it with a 700ms setTimeout).
 *
 * SDK-internal callers (WhatsOnChainCustom, CustomStateClient) are
 * constructed AFTER createSharedWhatsOnChainConnector inside the same
 * init_run_and_caches_from_privkeys flow, so they can never race and should
 * keep using the strict (synchronous, throwing) getSharedWhatsOnChainConnector().
 *
 * Resolves with the singleton once it exists. Rejects (after the retry budget
 * is exhausted — default ~30s with exponential backoff + jitter) if it still
 * isn't created, which indicates the wallet never finished loading.
 *
 * @param {object} [options] async-retry options. Defaults: retries=20,
 *   minTimeout=500ms, maxTimeout=5s, factor=2 (with jitter).
 */
export async function waitForSharedWhatsOnChainConnector(options = {}) {
    const log = getLog('waitForSharedWhatsOnChainConnector: ')
    return await AsyncRetry(async () => {
        return getSharedWhatsOnChainConnector()
    }, {
        retries: 20,
        minTimeout: 500,
        maxTimeout: 5000,
        factor: 2,
        randomize: true,
        onRetry: (attempt, error) => {
            log('connector not yet created (attempt ' + attempt + '), retrying:', error && error.message)
        },
        ...options,
    })
}
